<template>
	<ValidationObserver
		ref="reviewForm"
		v-slot="{ invalid, errors }"
		tag="form"
		class="product-overlay__review-form"
		@submit.prevent="onReviewSubmit"
		novalidate
	>
		<GeneralMessage
			v-if="reviewResponse.message"
			:message="reviewResponse.message"
			:type="reviewResponse.type"
			@close="reviewResponse.message = ''"
		/>

		<ValidationProvider
			name="email"
			rules="required|email"
			v-slot="{ errors, invalid, validated }"
			tag="div"
			:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
			v-if="!isLoggedIn"
		>
			<FormInput
				type="email"
				name="email"
				autocomplete="email"
				:placeholder="$t('forms.labels.email')"
				:label="$t('forms.labels.email')"
				v-model="reviewForm.email"
				:error="errors.length > 0"
				:readonly="!!isLoggedIn"
				:disabled="loading"
				required
				@input="onInput"
			/>
			<span
				class="input-error"
				v-if="invalid && validated"
				v-html="errors[0]"
			/>
			<span class="additional-info" v-html="$t('shoplist.product.details.ratings.emailHint')"/>
		</ValidationProvider>

		<ValidationProvider
			name="firstname"
			rules="required"
			v-slot="{ errors, invalid, validated }"
			tag="div"
			:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
		>
			<FormInput
				type="text"
				:placeholder="$t('shoplist.product.details.ratings.nameLabel')"
				:label="$t('shoplist.product.details.ratings.nameLabel')"
				name="name"
				v-model="reviewForm.title"
				:error="errors.length > 0"
				:disabled="loading"
				required
				@input="onInput"
			/>
			<span
				class="input-error"
				v-if="invalid && validated"
				v-html="errors[0]"
			/>
			<span class="additional-info" v-html="$t('shoplist.product.details.ratings.nameHint')"/>
		</ValidationProvider>

		<ValidationProvider
			name="comment"
			rules="required"
			v-slot="{ errors, invalid, validated }"
			tag="div"
			:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
		>
			<FormInputTextarea
				:placeholder="$t('shoplist.product.details.ratings.commentLabel')"
				:label="$t('shoplist.product.details.ratings.commentLabel')"
				name="comment"
				v-model="reviewForm.comment"
				:error="errors.length > 0"
				:disabled="loading"
				required
				@input="onInput"
			/>
			<span
				class="input-error"
				v-if="invalid && validated"
				v-html="errors[0]"
			/>
		</ValidationProvider>

		<ValidationProvider
			name="rating"
			rules="required"
			v-slot="{ errors, invalid, validated }"
			tag="div"
			:customMessages="{ required: $t('shoplist.product.details.ratings.errorRating') }"
		>
			<FormInputRating
				:legend="$t('shoplist.product.details.ratings.ratingLabel')"
				name="rating"
				v-model="reviewForm.rating"
				:error="errors.length > 0"
				:disabled="loading"
				required
				@input="onInput"
			/>
			<span
				class="input-error"
				v-if="invalid && validated"
				v-html="errors[0]"
			/>
		</ValidationProvider>

		<Button
			class="form-button"
			type="submit"
			level="primary"
			:text="$t('shoplist.product.details.ratings.formButton')"
			:disabled="loading"
		/>
		<div
			class="additional-info additional-info--align text--highlight"
			v-html="$t('shoplist.product.details.ratings.formHint')"
		/>
	</ValidationObserver>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ProductRating from '@/components/ProductRating';
import FormInput from '@/components/FormInput';
import FormInputTextarea from '@/components/FormInputTextarea';
import FormInputRating from '@/components/FormInputRating';
import Button from '@/components/Button';
import GeneralMessage from '@/components/GeneralMessage';
import GenericMixin from '@/mixins/GenericVueMixin'

export default {
  mixin: [
    GenericMixin,
  ],
	components: {
		ProductRating,
		ValidationObserver,
		ValidationProvider,
		FormInput,
		FormInputTextarea,
		FormInputRating,
		Button,
		GeneralMessage,
	},
	props: {
		code: [String, Number],
	},
	data() {
		return {
			reviewForm: {
				code: this.code,
				email: '',
				title: '',
				comment: '',
				rating: ''
			},
			reviewResponse: {
				message: '',
				type: '',
			},
			edited: false,
		};
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"accountUserDetail",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
	},
	methods: {
		onInput() {
			this.edited = true;
		},
		async onReviewSubmit() {
			this.loading = true;
			const isValid = await this.$refs.reviewForm.validate()

			if (isValid) {
				if (this.isLoggedIn) {
					this.reviewForm.email = this.accountUserDetail.email;
				}

				try {
					await this.$store.dispatch('productlist/sendProductReview', this.reviewForm)
					this.reviewResponse.message = this.$t('shoplist.product.details.ratings.success');
					this.reviewResponse.type = 'success';
					this.resetReview();
					this.$emit('reviewed')
				} catch(err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.onReviewSubmit(), retry: true })
          } else {
            let message = err.response.data ? err.response.data.message : this.$t('forms.errors.generic');
            this.reviewResponse.message = message;
            this.reviewResponse.type = 'error';
          }
				}
			}
			this.loading = false;
		},
		resetReview() {
			this.$refs.reviewForm.reset();
			this.reviewForm.email = '';
			this.reviewForm.title = '';
			this.reviewForm.comment = '';
			this.reviewForm.rating = '';
			this.edited = false;
		}
	},
}
</script>

<style>

</style>
